import { account, databases, databaseId, appwriteConfig } from '../config/appwrite';
import { User, UserRole } from '../types';
import { ID, Permission, Query, Role } from 'appwrite';

export class AuthService {
  // Benutzer anmelden
  static async login(email: string, password: string): Promise<User> {
    try {
      let currentUser;
      try {
        currentUser = await account.get();
      } catch (sessionError: any) {
        if (sessionError.code !== 401) throw sessionError;
      }
      if (currentUser?.email.toLowerCase() !== email.toLowerCase()) {
        if (currentUser) await account.deleteSession('current');
        await account.createEmailSession(email, password);
      }
      
      // Benutzerdaten abrufen
      const user = await this.getCurrentUser();
      return user;
    } catch (error: any) {
      
      // Bessere Fehlermeldungen basierend auf dem Appwrite-Fehler
      if (error.code === 401) {
        if (error.type === 'user_session_already_exists') {
          throw new Error('Du bist bereits angemeldet. Bitte melde dich zuerst ab.');
        } else {
          throw new Error('Ungültige E-Mail oder Passwort');
        }
      } else if (error.code === 404) {
        throw new Error('Benutzer nicht gefunden');
      } else if (error.message) {
        throw new Error(`Anmeldung fehlgeschlagen: ${error.message}`);
      } else {
        throw new Error('Anmeldung fehlgeschlagen. Bitte prüfe deine Eingaben.');
      }
    }
  }

  // Benutzer registrieren
  static async register(email: string, password: string, name: string): Promise<User> {
    try {
      await account.create(
        ID.unique(),
        email,
        password,
        name
      );

      // Ein vorhandener Login (etwa des Administrators) darf die Einrichtung nicht abbrechen.
      try {
        let signedIn = false;
        try {
          await account.get();
          signedIn = true;
        } catch (sessionError: any) {
          if (sessionError.code !== 401) throw sessionError;
        }
        if (signedIn) await account.deleteSession('current');
        await account.createEmailSession(email, password);
        return await this.getCurrentUser();
      } catch (setupError: any) {
        console.error('Einrichtung des neuen Kontos fehlgeschlagen.', { code: setupError.code, type: setupError.type, message: setupError.message });
        throw new Error('Dein Login-Konto wurde erstellt, aber die Einrichtung konnte nicht abgeschlossen werden. Bitte melde dich erneut an, um dein Benutzerprofil einzurichten.');
      }
    } catch (error: any) {
      
      // Bessere Fehlermeldungen basierend auf dem Appwrite-Fehler
      if (error.code === 409) {
        throw new Error('Ein Benutzer mit dieser E-Mail-Adresse existiert bereits');
      } else if (error.code === 400) {
        throw new Error(`Ungültige Eingabedaten: ${error.message}`);
      } else if (error.code === 404) {
        throw new Error('Die Registrierung ist derzeit nicht verfügbar. Bitte kontaktiere die Administration.');
      } else if (error.message) {
        throw new Error(`Registrierung fehlgeschlagen: ${error.message}`);
      } else {
        throw new Error('Registrierung fehlgeschlagen. Bitte versuche es erneut oder kontaktiere die Administration.');
      }
    }
  }

  // Benutzer abmelden
  static async logout(): Promise<void> {
    try {
      await account.deleteSession('current');
    } catch (error) {
      throw new Error('Abmeldung fehlgeschlagen');
    }
  }

  // Aktuellen Benutzer abrufen
  static async getCurrentUser(): Promise<User> {
    try {
      const currentUser = await account.get();
      
      // Ein vollständiges, gespeichertes Profil ist für Verwaltung und Berechtigungen erforderlich.
      try {
        const userDoc = await this.getOrCreateUserProfile(currentUser);

        return {
          id: userDoc.$id,
          email: userDoc.email,
          name: userDoc.name,
          roles: userDoc.roles && userDoc.roles.length > 0 ? userDoc.roles : ['autor'],
          assignedPlatforms: userDoc.assignedPlatforms || [],
          assignedSchedules: userDoc.assignedSchedules || [],
          preferredLektoratIntensity: userDoc.preferredLektoratIntensity || 'leicht',
          createdAt: new Date(userDoc.$createdAt),
          updatedAt: new Date(userDoc.$updatedAt),
        };
      } catch (docError: any) {
        console.error('Benutzerprofil konnte nicht eingerichtet werden.', { code: docError.code, type: docError.type, message: docError.message });
        throw new Error('Dein Benutzerprofil konnte nicht geladen oder gespeichert werden. Bitte versuche die Anmeldung erneut.');
      }
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Benutzerdaten konnten nicht abgerufen werden');
    }
  }

  private static async getOrCreateUserProfile(user: { $id: string; email: string; name: string }) {
    try {
      return await databases.getDocument(databaseId, appwriteConfig.collections.users, user.$id);
    } catch (error: any) {
      if (error.code !== 404) throw error;
    }

    try {
      return await databases.createDocument(databaseId, appwriteConfig.collections.users, user.$id, {
        email: user.email,
        name: user.name,
        roles: ['autor'],
        assignedPlatforms: [],
        assignedSchedules: []
      }, [Permission.read(Role.user(user.$id)), Permission.update(Role.user(user.$id)), Permission.delete(Role.user(user.$id))]);
    } catch (error: any) {
      // Bei gleichzeitigen Anmeldungen kann ein anderer Aufruf das Profil bereits angelegt haben.
      if (error.code !== 409) throw error;
      return databases.getDocument(databaseId, appwriteConfig.collections.users, user.$id);
    }
  }

  // Benutzer nach ID abrufen
  static async getUserById(userId: string): Promise<User> {
    try {
      const userDoc = await databases.getDocument(
        databaseId,
        appwriteConfig.collections.users,
        userId
      );

      return {
        id: userDoc.$id,
        email: userDoc.email,
        name: userDoc.name,
        roles: userDoc.roles && userDoc.roles.length > 0 ? userDoc.roles : ['autor'],
        assignedPlatforms: userDoc.assignedPlatforms || [],
        assignedSchedules: userDoc.assignedSchedules || [],
        preferredLektoratIntensity: userDoc.preferredLektoratIntensity || 'leicht',
        createdAt: new Date(userDoc.$createdAt),
        updatedAt: new Date(userDoc.$updatedAt),
      };
    } catch (error) {
      throw new Error('Benutzer konnte nicht abgerufen werden');
    }
  }

  // Mehrere Benutzer nach IDs abrufen (Batch-Query für Performance)
  static async getUsersByIds(userIds: string[]): Promise<User[]> {
    try {
      if (userIds.length === 0) {
        return [];
      }

      const response = await databases.listDocuments(
        databaseId,
        appwriteConfig.collections.users,
        [
          Query.equal('$id', userIds),
          Query.limit(100)
        ]
      );

      return response.documents.map(doc => ({
        id: doc.$id,
        email: doc.email,
        name: doc.name,
        roles: doc.roles && doc.roles.length > 0 ? doc.roles : ['autor'],
        assignedPlatforms: doc.assignedPlatforms || [],
        assignedSchedules: doc.assignedSchedules || [],
        preferredLektoratIntensity: doc.preferredLektoratIntensity || 'leicht',
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      }));
    } catch (error) {
      console.error('Fehler beim Batch-Laden der Benutzer:', error);
      throw new Error('Benutzer konnten nicht abgerufen werden');
    }
  }

  // Benutzerrolle zuweisen
  static async assignUserRole(userId: string, role: UserRole): Promise<void> {
    try {
      await databases.updateDocument(
        databaseId,
        appwriteConfig.collections.users,
        userId,
        {
          roles: [role],
        }
      );
    } catch (error) {
      throw new Error('Rolle konnte nicht zugewiesen werden');
    }
  }

  // Benutzer-Rollen aktualisieren
  static async updateUserRole(userId: string, roles: UserRole[]): Promise<void> {
    try {
      await databases.updateDocument(
        databaseId,
        appwriteConfig.collections.users,
        userId,
        { roles }
      );
    } catch (error) {
      throw new Error('Benutzer-Rolle konnte nicht aktualisiert werden');
    }
  }

  // Benutzerrolle entfernen
  static async removeUserRole(userId: string, role: UserRole): Promise<void> {
    try {
      const userDoc = await databases.getDocument(
        databaseId,
        appwriteConfig.collections.users,
        userId
      );

      const updatedRoles = userDoc.roles.filter((r: string) => r !== role);

      await databases.updateDocument(
        databaseId,
        appwriteConfig.collections.users,
        userId,
        {
          roles: updatedRoles,
        }
      );
    } catch (error) {
      throw new Error('Rolle konnte nicht entfernt werden');
    }
  }

  // Plattform einem Benutzer zuweisen
  static async assignPlatformToUser(userId: string, platformId: string): Promise<void> {
    try {
      const userDoc = await databases.getDocument(
        databaseId,
        appwriteConfig.collections.users,
        userId
      );

      const updatedPlatforms = [...(userDoc.assignedPlatforms || []), platformId];

      await databases.updateDocument(
        databaseId,
        appwriteConfig.collections.users,
        userId,
        {
          assignedPlatforms: updatedPlatforms,
        }
      );
    } catch (error) {
      throw new Error('Plattform konnte nicht zugewiesen werden');
    }
  }

  // Plattform von einem Benutzer entfernen
  static async removePlatformFromUser(userId: string, platformId: string): Promise<void> {
    try {
      const userDoc = await databases.getDocument(
        databaseId,
        appwriteConfig.collections.users,
        userId
      );

      const updatedPlatforms = userDoc.assignedPlatforms.filter((id: string) => id !== platformId);

      await databases.updateDocument(
        databaseId,
        appwriteConfig.collections.users,
        userId,
        {
          assignedPlatforms: updatedPlatforms,
        }
      );
    } catch (error) {
      throw new Error('Plattform konnte nicht entfernt werden');
    }
  }

  // Prüfen, ob Benutzer angemeldet ist
  static async isAuthenticated(): Promise<boolean> {
    try {
      const currentUser = await account.get();
      return !!currentUser && !!currentUser.$id;
    } catch (error) {
      return false;
    }
  }

  // Alle Benutzer abrufen (nur für Administratoren/Redakteure)
  static async getAllUsers(): Promise<User[]> {
    try {
      const response = await databases.listDocuments(
        databaseId,
        appwriteConfig.collections.users,
        [Query.orderDesc('$createdAt'), Query.limit(100)]
      );

      return response.documents.map((doc: any) => ({
        id: doc.$id,
        email: doc.email,
        name: doc.name,
        roles: doc.roles && doc.roles.length > 0 ? doc.roles : ['autor'],
        assignedPlatforms: doc.assignedPlatforms || [],
        assignedSchedules: doc.assignedSchedules || [],
        preferredLektoratIntensity: doc.preferredLektoratIntensity || 'leicht',
        createdAt: new Date(doc.$createdAt),
        updatedAt: new Date(doc.$updatedAt),
      }));
    } catch (error) {
      throw new Error('Benutzer konnten nicht abgerufen werden');
    }
  }

  // Benutzer-Präferenzen aktualisieren
  static async updateUserPreferences(userId: string, preferences: Partial<User>): Promise<User> {
    try {
      const updatedUser = await databases.updateDocument(
        databaseId,
        appwriteConfig.collections.users,
        userId,
        preferences
      );

      return {
        id: updatedUser.$id,
        email: updatedUser.email,
        name: updatedUser.name,
        roles: updatedUser.roles && updatedUser.roles.length > 0 ? updatedUser.roles : ['autor'],
        assignedPlatforms: updatedUser.assignedPlatforms || [],
        assignedSchedules: updatedUser.assignedSchedules || [],
        preferredLektoratIntensity: updatedUser.preferredLektoratIntensity || 'leicht',
        lektoratHinweise: updatedUser.lektoratHinweise || [],
        lektoratHinweiseEnabled: updatedUser.lektoratHinweiseEnabled || false,
        createdAt: new Date(updatedUser.$createdAt),
        updatedAt: new Date(updatedUser.$updatedAt),
      };
    } catch (error) {
      throw new Error('Fehler beim Aktualisieren der Benutzer-Präferenzen');
    }
  }

  // Lektorat-Hinweise speichern
  static async saveLektoratHinweise(userId: string, hinweise: string[]): Promise<void> {
    try {
      await databases.updateDocument(
        databaseId,
        appwriteConfig.collections.users,
        userId,
        {
          lektoratHinweise: hinweise,
        }
      );
    } catch (error) {
      throw new Error('Fehler beim Speichern der Lektorat-Hinweise');
    }
  }

  // Lektorat-Hinweise laden
  static async getLektoratHinweise(userId: string): Promise<string[]> {
    try {
      const user = await databases.getDocument(
        databaseId,
        appwriteConfig.collections.users,
        userId
      );
      return user.lektoratHinweise || [];
    } catch (error) {
      throw new Error('Fehler beim Laden der Lektorat-Hinweise');
    }
  }

  // Lektorat-Hinweise-Einstellung aktualisieren
  static async updateLektoratHinweiseEnabled(userId: string, enabled: boolean): Promise<void> {
    try {
      await databases.updateDocument(
        databaseId,
        appwriteConfig.collections.users,
        userId,
        {
          lektoratHinweiseEnabled: enabled,
        }
      );
    } catch (error) {
      throw new Error('Fehler beim Aktualisieren der Lektorat-Hinweise-Einstellung');
    }
  }

  // Passwort ändern
  static async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      // Passwort-Update über Appwrite Account API
      // Appwrite erfordert das aktuelle Passwort als zusätzlichen Parameter
      await account.updatePassword(newPassword, currentPassword);
    } catch (error: any) {
      
      // Bessere Fehlermeldungen basierend auf dem Appwrite-Fehler
      if (error.code === 401) {
        throw new Error('Aktuelles Passwort ist nicht korrekt oder du bist nicht angemeldet');
      } else if (error.code === 400) {
        if (error.message && error.message.includes('password')) {
          throw new Error('Das neue Passwort entspricht nicht den Anforderungen');
        } else {
          throw new Error(`Ungültige Eingabedaten: ${error.message || 'Unbekannter Fehler'}`);
        }
      } else if (error.code === 409) {
        throw new Error('Passwort-Änderung nicht erlaubt');
      } else if (error.message) {
        throw new Error(`Passwort-Änderung fehlgeschlagen: ${error.message}`);
      } else {
        throw new Error('Passwort-Änderung fehlgeschlagen. Bitte versuche es erneut.');
      }
    }
  }

  // Passwort-Reset E-Mail anfordern
  static async requestPasswordReset(email: string): Promise<void> {
    try {
      const redirectUrl = `${window.location.origin}/reset-password`;
      await account.createRecovery(email, redirectUrl);
    } catch (error: any) {
      if (error.code === 404) {
        throw new Error('Benutzer nicht gefunden');
      } else if (error.code === 429) {
        throw new Error('Zu viele Anfragen. Bitte versuche es später erneut.');
      } else if (error.code === 400) {
        throw new Error(`Ungültige Anfrage: ${error.message || 'Bitte Eingaben prüfen.'}`);
      } else if (error.message) {
        throw new Error(`Passwort-Reset konnte nicht gestartet werden: ${error.message}`);
      } else {
        throw new Error('Passwort-Reset konnte nicht gestartet werden.');
      }
    }
  }

  // Neues Passwort über Recovery-Link setzen
  static async confirmPasswordReset(userId: string, secret: string, password: string): Promise<void> {
    try {
      await account.updateRecovery(userId, secret, password, password);
    } catch (error: any) {
      if (error.code === 401) {
        throw new Error('Der Reset-Link ist ungültig oder abgelaufen.');
      } else if (error.code === 400) {
        throw new Error(`Ungültige Eingabe: ${error.message || 'Bitte Passwortanforderungen prüfen.'}`);
      } else if (error.message) {
        throw new Error(`Passwort konnte nicht zurückgesetzt werden: ${error.message}`);
      } else {
        throw new Error('Passwort konnte nicht zurückgesetzt werden.');
      }
    }
  }
}
